import React, { useState, useRef, useMemo, } from 'react';
import { useFetchWithBody, usePost } from '../../api/reactQueryCustomHooks';
import {
  Box, Button, Grid, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableFooter, TableRow, TextField, Typography, FormControl, InputLabel,
  Select, MenuItem, IconButton, InputAdornment, Popover, useMediaQuery, Chip, Alert, AlertTitle
} from '@mui/material';
import { format } from 'date-fns';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { apiRoute } from '../../constants/enum';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { jsPDF } from 'jspdf';
import { autoTable } from "jspdf-autotable"
import 'jspdf-autotable';
import CircularProgress from '@mui/material/CircularProgress';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faDownload, faFilter, faInbox, faInfo, faRefresh, faSearch } from '@fortawesome/free-solid-svg-icons';
import { ErrorCard, LoadingCard, MainContainer } from '../../components';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import dayjs from "dayjs"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { isPending } from '@reduxjs/toolkit';
import { head } from 'lodash';

const CreatePaymentReport = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { compCode, startDate, endDate } = useSelector((state) => state.company);
  const tableRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentFilterColumn, setCurrentFilterColumn] = useState('');
  const [groupBy, setGroupBy] = useState('none');
  const [manualFilters, setManualFilters] = useState(null);
  const [columnFilters, setColumnFilters] = useState({
    Chq_Date: '',
    AC_DESC: '',
    PAc_Desc: '',
    BankName: '',
    Chq_No: '',
    Chq_Amt: '',
    Remark: '',
  });

  const [filters, setFilters] = useState({
    compCode: compCode,
    dy: '1',
    fromDate: startDate,//new Date(new Date().setDate(new Date().getDate() - 30)),
    toDate: endDate,// new Date(),
    Vou_Type: 'R',
    AC_ID: ''
  });

  const { data: accounts = [] } = useFetchWithBody({
    url: apiRoute.transactionEntry.getParties,
    queryKey: ['paymentReportAccounts'],
    inputData: { compCode }
  });

  const {
    data: reportData = [],
    isPending: isLoading,
    isError,
    error,
    mutate: fetchReportData
  } = usePost({
    url: apiRoute.transactionEntry.getReportData,
    queryKey: ['paymentReport', manualFilters],
  });


  // Apply column filters to the data
  const filteredData = useMemo(() => {
    if (!reportData.length) return [];
    return reportData.filter(row => {
      return Object.entries(columnFilters).every(([key, value]) => {
        if (!value) return true;
        const rowValue = row[key] || '';
        return String(rowValue).toLowerCase().includes(String(value).toLowerCase());
      });
    });
  }, [reportData, columnFilters]);

  const filteredAndGroupedData = useMemo(() => {
    if (!reportData.length) return [];

    // First apply column filters
    let data = reportData.filter(row => {
      return Object.entries(columnFilters).every(([key, value]) => {
        if (!value) return true;
        const rowValue = row[key] || '';
        return String(rowValue).toLowerCase().includes(String(value).toLowerCase());
      });
    });

    // Then apply grouping if selected
    if (groupBy !== 'none') {
      const groupedData = {};

      data.forEach(row => {
        let groupKey;

        switch (groupBy) {
          case 'account':
            groupKey = row.AC_DESC || 'Account';
            break;
          case 'party':
            groupKey = row.PAc_Desc || 'Party';
            break;
          case 'date':
            groupKey = format(new Date(row.Chq_Date), 'yyyy-MM-dd');
            break;
          case 'month':
            groupKey = format(new Date(row.Chq_Date), 'yyyy-MM');
            break;
          default:
            groupKey = 'all';
        }

        if (!groupedData[groupKey]) {
          groupedData[groupKey] = {
            groupKey,
            Chq_Date: groupBy === 'date'
              ? format(new Date(row.Chq_Date), 'dd-MMM-yyyy')
              : groupBy === 'month'
                ? format(new Date(row.Chq_Date), 'MMMM yyyy')
                : groupKey,
            AC_DESC: groupBy === 'account' ? row.AC_DESC : 'Multiple Accounts',
            PAc_Desc: groupBy === 'party' ? row.PAc_Desc : 'Multiple Parties',
            BankName: groupBy === 'bank' ? row.BankName : 'Multiple Banks',
            Chq_No: groupBy === 'none' ? row.Chq_No : 'Multiple',
            Chq_Amt: 0,
            Remark: groupBy === 'none' ? row.Remark : 'Multiple Remarks',
            _children: []
          };
        }

        // Sum up all numeric values
        groupedData[groupKey].Chq_Amt += parseFloat(row.Chq_Amt) || 0;

        // Store original rows for drill-down
        groupedData[groupKey]._children.push(row);
      });

      // Convert to array and sort
      data = Object.values(groupedData).sort((a, b) => {
        if (groupBy === 'date' || groupBy === 'month') {
          return new Date(a.Chq_Date) - new Date(b.Chq_Date);
        }
        return a.groupKey.localeCompare(b.groupKey);
      });
    }

    return data;
  }, [reportData, columnFilters, groupBy]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };



  const handleColumnFilterChange = (e) => {
    const { value } = e.target;
    setColumnFilters(prev => ({ ...prev, [currentFilterColumn]: value }));
  };

  const handleSearch = () => {
    const params = {
      ...filters,
      fromDate: format(filters.fromDate, 'yyyy-MM-dd'),
      toDate: format(filters.toDate, 'yyyy-MM-dd'),
      AC_ID: filters.AC_ID || ''
    };
    fetchReportData({ data: params });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

  };

  const handleReset = () => {
    setFilters({
      compCode: compCode.join(","),
      dy: '1',
      fromDate: startDate,// new Date(new Date().setDate(new Date().getDate() - 30)),
      toDate: endDate,// new Date(),
      Vou_Type: 'R'
    });
    const params = {
      compCode: compCode.join(","),
      dy: '1',
      Vou_Type: 'R',
      fromDate: startDate,
      toDate: endDate,
      AC_ID: filters.AC_ID || ''
    };
    fetchReportData({ data: params });
  };

  const handleFilterClick = (columnName, event) => {
    setCurrentFilterColumn(columnName);
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const clearColumnFilter = (columnName) => {
    setColumnFilters(prev => ({ ...prev, [columnName]: '' }));
  };

  const calculateTotals = () => {
    const totals = {
      Chq_Amt: 0,
    };

    filteredData.forEach(row => {
      totals.Chq_Amt += parseFloat(row.Chq_Amt) || 0;
    });

    return totals;
  };

  const exportToExcel = () => {
    if (filteredData.length === 0) return;

    const totals = calculateTotals();

    const columnsToShow = [];
    if (groupBy === 'none') {
      columnsToShow.push(
        { key: 'Chq_Date', label: 'Date', width: 15 },
        { key: 'AC_DESC', label: 'Account', width: 25 },
        { key: 'PAc_Desc', label: 'Party', width: 25 },
        { key: 'BankName', label: 'Bank', width: 20 },
        { key: 'Chq_No', label: 'Cheque No', width: 15 },
        { key: 'Chq_Amt', label: 'Amount', width: 15 },
        { key: 'Remark', label: 'Remark', width: 30 },
      );
    } else if (groupBy === 'account') {
      columnsToShow.push(
        { key: 'AC_DESC', label: 'Account', width: 30 },
        { key: 'Chq_Amt', label: 'Amount', width: 20 },
      );
    } else if (groupBy === 'party') {
      columnsToShow.push(
        { key: 'PAc_Desc', label: 'Party', width: 30 },
        { key: 'Chq_Amt', label: 'Amount', width: 20 },
      );
    } else if (groupBy === 'bank') {
      columnsToShow.push(
        { key: 'BankName', label: 'Bank', width: 30 },
        { key: 'Chq_Amt', label: 'Amount', width: 20 },
      );
    } else if (groupBy === 'date') {
      columnsToShow.push(
        { key: 'Chq_Date', label: 'Date', width: 15 },
        { key: 'Chq_Amt', label: 'Amount', width: 20 },
      );
    } else if (groupBy === 'month') {
      columnsToShow.push(
        { key: 'Chq_Date', label: 'Month', width: 15 },
        { key: 'Chq_Amt', label: 'Amount', width: 20 },
      );
    }

    const headers = columnsToShow.map(col => col.label);

    const rows = filteredAndGroupedData.map(row => {
      return columnsToShow.map(col => {
        const value = row[col.key];
        if (col.key === 'Chq_Date') {
          if (!value) return '';
          const dateObj = new Date(value);
          if (isNaN(dateObj.getTime())) return '';
          return groupBy === 'month' ? format(dateObj, 'MMM yyyy') : format(dateObj, 'dd/MM/yyyy');
        }
        if (typeof value === 'number') return value.toFixed(2);
        return value ?? '';
      });
    });

    // Add totals row
    const totalRow = columnsToShow.map(col => {
      switch (col.key) {
        case 'Chq_Amt': return totals.Chq_Amt?.toFixed(2) || '';
        default: return '';
      }
    });
    totalRow[0] = 'TOTAL';
    rows.push(totalRow);

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    // Set column widths
    ws['!cols'] = columnsToShow.map(col => ({ wch: col.width }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');

    const fileName = `${filters.Vou_Type === 'R' ? 'Receive' : 'Payment'}_Register_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`;
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), fileName);
  };

  const compName = useSelector((state) => state.company.compName);
  const selectedCompanyName = compName.length > 0 ? compName[0] : "N/A";

  const exportToPDF = () => {
    if (filteredData.length === 0) return;

    const totals = calculateTotals();
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm'
    });

    // Set default font
    doc.setFont('helvetica');
    doc.setFontSize(10);

    // Page dimensions
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    const contentWidth = pageWidth - 2 * margin;

    // Company Name - Top Center (Large and bold)
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    const companyName = compName.length > 0 ? compName.join(', ') : 'Company';
    const companyNameWidth = doc.getStringUnitWidth(companyName) * doc.internal.getFontSize() / doc.internal.scaleFactor;
    doc.text(companyName, margin + (contentWidth - companyNameWidth) / 2, 20);

    // Report Title - Below Company Name
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    const reportTitle = `${filters.Vou_Type === 'R' ? 'Receive' : 'Payment'} Register`;
    const reportTitleWidth = doc.getStringUnitWidth(reportTitle) * doc.internal.getFontSize() / doc.internal.scaleFactor;
    doc.text(reportTitle, margin + (contentWidth - reportTitleWidth) / 2, 27);

    // Date Range - Left aligned
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    const fromText = `From: ${format(filters.fromDate, 'dd/MM/yyyy')}`;
    const toText = `To: ${format(filters.toDate, 'dd/MM/yyyy')}`;

    // Initial position
    const marginX = margin;
    const marginY = 40;

    // Write "From" text
    doc.text(fromText, marginX, marginY);

    // Calculate width of the "From" text to position "To" text right after it
    const fromTextWidth = doc.getTextWidth(fromText);

    // Add spacing between the two texts (e.g., 10 units)
    doc.text(toText, marginX + fromTextWidth + 10, marginY);

    // Add a separator line
    doc.setDrawColor(100, 100, 100);
    doc.line(margin, 44, pageWidth - margin, 44);

    const columnsToShow = [];
    if (groupBy === 'none') {
      columnsToShow.push(
        { key: 'Chq_Date', label: 'Date', width: 25 },
        { key: 'AC_DESC', label: 'Account', width: 40 },
        { key: 'PAc_Desc', label: 'Party', width: 30 },
        { key: 'BankName', label: 'Bank', width: 20 },
        { key: 'Chq_No', label: 'Cheque No', width: 20 },
        { key: 'Chq_Amt', label: 'Amount', width: 30 },
        { key: 'Remark', label: 'Remark', width: 40 },
      );
    } else if (groupBy === 'account') {
      columnsToShow.push(
        { key: 'AC_DESC', label: 'Account', width: 40 },
        { key: 'Chq_Amt', label: 'Amount', width: 30 },
      );
    } else if (groupBy === 'party') {
      columnsToShow.push(
        { key: 'PAc_Desc', label: 'Party', width: 40 },
        { key: 'Chq_Amt', label: 'Amount', width: 30 },
      );
    } else if (groupBy === 'bank') {
      columnsToShow.push(
        { key: 'BankName', label: 'Bank', width: 40 },
        { key: 'Chq_Amt', label: 'Amount', width: 30 },
      );
    } else if (groupBy === 'date') {
      columnsToShow.push(
        { key: 'Chq_Date', label: 'Date', width: 40 },
        { key: 'Chq_Amt', label: 'Amount', width: 30 },
      );
    } else if (groupBy === 'month') {
      columnsToShow.push(
        { key: 'Chq_Date', label: 'Month', width: 40 },
        { key: 'Chq_Amt', label: 'Amount', width: 30 },
      );
    }

    const headers = columnsToShow.map(col => col.label);
    const columnStyles = {};
    columnsToShow.forEach((col, index) => {
      columnStyles[index] = {
        cellWidth: col.width,
        overflow: 'linebreak',
        minCellHeight: 5
      };
    });

    const data = filteredAndGroupedData.map(row => {
      return columnsToShow.map(col => {
        const value = row[col.key];
        if (col.key === 'Chq_Date') {
          return groupBy === 'month' ? format(new Date(value), 'MMM yyyy') : format(new Date(value), 'dd/MM/yyyy');
        }
        if (typeof value === 'number') {
          // Format numbers with commas for thousands
          return value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }
        return value ?? '';
      });
    });

    // Add totals row
    const totalRow = columnsToShow.map(col => {
      switch (col.key) {
        case 'Chq_Amt': return totals.Chq_Amt?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '';
        default: return '';
      }
    });
    totalRow[0] = 'TOTAL';
    data.push(totalRow);

    // AutoTable configuration
    autoTable(doc, {
      head: [headers],
      body: data,
      startY: 47, // Start below the header section
      margin: { left: margin, right: margin },
      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: 'linebreak',
        halign: 'right',
        valign: 'middle',
        lineWidth: 0.1,
        lineColor: [200, 200, 200],
        font: 'helvetica',
        textColor: [0, 0, 0]
      },
      columnStyles,
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontSize: 8,
        halign: 'center',
        valign: 'middle',
        fontStyle: 'bold'
      },
      bodyStyles: {
        halign: 'right',
        valign: 'middle',
        fontSize: 8
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      didParseCell: function (dataCell) {
        // First column left aligned
        if (dataCell.column.index === 0) {
          dataCell.cell.styles.halign = 'left';
        }

        // Bold and centered for totals row
        if (dataCell.row.index === filteredAndGroupedData.length) {
          dataCell.cell.styles.fontStyle = 'bold';
          dataCell.cell.styles.fillColor = [220, 220, 220];
          dataCell.cell.styles.textColor = [0, 0, 0];
          if (dataCell.column.index === 0) {
            dataCell.cell.styles.halign = 'center';
          }
        }

        // Prevent word breaking in headers
        if (dataCell.row.index === 0) {
          dataCell.cell.styles.cellPadding = { top: 2, right: 2, bottom: 2, left: 2 };
        }

        // Better handling of long text
        if (typeof dataCell.cell.raw === 'string' && dataCell.cell.raw.length > 15) {
          dataCell.cell.styles.fontSize = 7;
        }
      },
      willDrawCell: function (dataCell) {
        // Add borders to all cells
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.1);
        doc.line(
          dataCell.cell.x,
          dataCell.cell.y + dataCell.cell.height,
          dataCell.cell.x + dataCell.cell.width,
          dataCell.cell.y + dataCell.cell.height
        );
      },
      willDrawPage: function (data) {
        // Add page numbers
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, doc.internal.pageSize.getHeight() - 10, { align: 'right' });
        }
      }
    });

    const filename = `${selectedCompanyName}_${filters.Vou_Type === 'R' ? 'Receive' : 'Payment'}_Register_${format(new Date(), 'dd/MM/yyyy')}.pdf`;
    doc.save(filename);
  };

  const totals = calculateTotals();

  const getReportTitle = () => {
    return filters.Vou_Type === 'R' ? 'Receive Register' : 'Payment Register';
  };

  const formatDateSafe = (dateString, formatStr = 'dd/MM/yyyy') => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? '' : format(date, formatStr);
  };

  const allColumns = [
    { key: 'Chq_Date', label: 'Date' },
    { key: 'AC_DESC', label: 'Account' },
    { key: 'PAc_Desc', label: 'Party' },
    { key: 'BankName', label: 'Bank' },
    { key: 'Chq_No', label: 'Cheque No' },
    { key: 'Chq_Amt', label: 'Amount' },
    { key: 'Remark', label: 'Remark' },
  ];
    const headCssStyle = { fontSize: "0.7rem", lineHeight: 1.3, textTransform: "uppercase", backgroundColor: '#f0f0f0', fontWeight: 600, color: '#3c3c3c' }
    const headfilterStyle = "text-xs bg-slate-600 radius-md  p-0.5 ml-1 text-white"
  if (isError) {
    return <ErrorCard />
  }
  if (isLoading) {
    return <LoadingCard />
  }
  return (
    <MainContainer
      pageName={`Receive / Payment List`}
      breadcrumbs={["Reports", "Receive / Payment List"]}

    >
      <Box sx={{
        p: isMobile ? 1 : 3,
        backgroundColor: 'background.default',
        minHeight: '100vh'
      }}>
        {/* Header Section */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: 2,
          mb: 1,
          p: 1,
          backgroundColor: 'background.paper',
          borderRadius: 3,
          boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08)'
        }}>
          <Typography variant="h4" sx={{
            fontSize: isMobile ? '1rem' : '1.3rem',
            fontWeight: 500,
            color: 'primary.main',
            letterSpacing: -0.5
          }}>
            {getReportTitle()}
          </Typography>

          {reportData.length > 0 && (
            <Box sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              alignSelf: { xs: 'stretch', md: 'center' }
            }}>
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={<FontAwesomeIcon icon={faDownload} className='text-xs' />}
                onClick={exportToExcel}
                disabled={filteredData.length === 0}
                sx={{
                  textTransform: 'none',
                  fontWeight: 400,
                  px: 3,
                  borderRadius: 2,
                  boxShadow: 'none',
                  '&:hover': { boxShadow: '0px 3px 5px rgba(0, 0, 0, 0.2)' }
                }}
              >
                Export Excel
              </Button>

              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={<FontAwesomeIcon icon={faDownload} className='text-xs' />}
                onClick={exportToPDF}
                disabled={filteredData.length === 0}
                sx={{
                  textTransform: 'none',
                  fontWeight: 400,
                  px: 3,
                  borderRadius: 2,
                  boxShadow: 'none',
                  '&:hover': { boxShadow: '0px 3px 5px rgba(0, 0, 0, 0.2)' }
                }}
              >
                Export PDF
              </Button>
            </Box>
          )}
        </Box>

        {/* Filters Section */}
        <Paper sx={{
          p: { xs: 2, sm: 2 },
          mb: 1,
          borderRadius: 3,
          boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08)',
          backgroundColor: 'background.paper'
        }}>
          <form onSubmit={(e) => { e.preventDefault(); handleSearch(); }}>
            <Grid container spacing={2} alignItems="center">
              {/* Hidden fields */}
              <Grid item xs={12} sm={6} md={3} sx={{ display: 'none' }}>
                <TextField fullWidth label="Company Code" name="compCode" value={filters.compCode} />
              </Grid>
              <Grid item xs={12} sm={6} md={3} sx={{ display: 'none' }}>
                <TextField fullWidth label="DY" name="dy" value={filters.dy} />
              </Grid>

              {/* Main form controls */}
              <Grid item xs={12} sm={6} md={3} lg={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Voucher Type</InputLabel>
                  <Select
                    name="Vou_Type"
                    value={filters.Vou_Type}
                    onChange={handleFilterChange}
                    label="Voucher Type"
                    sx={{
                      '& .MuiSelect-select': { py: 1 }
                    }}
                  >
                    <MenuItem value="R">Receive Register</MenuItem>
                    <MenuItem value="G">Payment Register</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3} lg={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Account</InputLabel>
                  <Select
                    name="AC_ID"
                    value={filters.AC_ID}
                    onChange={handleFilterChange}
                    label="Account"
                    sx={{
                      '& .MuiSelect-select': { py: 1 }
                    }}
                  >
                    <MenuItem value="">All Accounts</MenuItem>
                    {accounts.map((account) => (
                      <MenuItem key={account.AC_ID} value={account.AC_ID}>
                        {account.AC_DESC}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3} lg={2}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label={"From Date"}
                    views={["day", "month", "year"]}
                    format="DD/MM/YYYY"
                    value={
                      filters.fromDate
                        ? dayjs(filters.fromDate)
                        : filters.fromDate
                    }
                    onChange={(newValue) => {
                      setFilters((prev) => {
                        return {
                          ...prev,
                          fromDate: newValue != '' && newValue != 'Invalid Date' ?
                            dayjs(newValue).format("YYYY-MM-DD") : null,
                        };
                      });
                    }}
                    //isRequired={item.required}
                    // className="w-full "
                    slotProps={{
                      textField: { size: "small", },
                    }}
                  />
                </LocalizationProvider>
                {/* <DatePicker
                  selected={filters.fromDate}
                  onChange={(date) => handleDateChange(date, 'fromDate')}
                  selectsStart
                  dateFormat="dd/MM/yyyy"
                  popperClassName="z-[9999]"
                  startDate={filters.fromDate}
                  endDate={filters.toDate}
                  customInput={
                    <TextField
                      fullWidth
                      label="From Date"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        '& .MuiInputBase-input': { py: 1 }
                      }}
                    />
                  }
                /> */}
              </Grid>

              <Grid item xs={12} sm={6} md={3} lg={2}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label={"To Date"}
                    views={["day", "month", "year"]}
                    format="DD/MM/YYYY"
                    value={
                      filters.toDate
                        ? dayjs(filters.toDate)
                        : filters.toDate
                    }
                    onChange={(newValue) => {
                      setFilters((prev) => {
                        return {
                          ...prev,
                          toDate: newValue != '' && newValue != 'Invalid Date' ?
                            dayjs(newValue).format("YYYY-MM-DD") : null,
                        };
                      });
                    }}
                    //isRequired={item.required}
                    //className="w-full "
                    slotProps={{
                      textField: { size: "small", },
                    }}
                  />
                </LocalizationProvider>
                {/* <DatePicker
                  selected={filters.toDate}
                  onChange={(date) => handleDateChange(date, 'toDate')}
                  selectsEnd
                  dateFormat="dd/MM/yyyy"
                  popperClassName="z-[9999]"
                  startDate={filters.fromDate}
                  endDate={filters.toDate}
                  minDate={filters.fromDate}
                  customInput={
                    <TextField
                      fullWidth
                      label="To Date"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        '& .MuiInputBase-input': { py: 1 }
                      }}
                    />
                  }
                /> */}
              </Grid>

              <Grid item xs={12} sm={6} md={3} lg={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Group By</InputLabel>
                  <Select
                    label="Group By"
                    value={groupBy}
                    onChange={(e) => setGroupBy(e.target.value)}
                    sx={{
                      '& .MuiSelect-select': { py: 1 }
                    }}
                  >
                    <MenuItem value="none">Report Detail</MenuItem>
                    <MenuItem value="account">Account</MenuItem>
                    <MenuItem value="party">Party</MenuItem>
                    <MenuItem value="date">Date</MenuItem>
                    <MenuItem value="month">Month</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Action buttons - responsive behavior */}
              <Grid item xs={12} sm={6} md={3} lg={2}>
                <Box sx={{
                  display: 'flex',
                  gap: 1,
                  flexDirection: { xs: 'row', sm: 'row-reverse' },
                  justifyContent: { xs: 'flex-start', sm: 'flex-end' }
                }}>


                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleReset}
                    startIcon={<FontAwesomeIcon className="text-xs" icon={faRefresh} />}
                    sx={{

                      minWidth: 80,
                      //py: 1,
                      borderRadius: 2,
                      fontWeight: 500,
                      textTransform: 'none',
                      borderWidth: 1.5,
                      '&:hover': { borderWidth: 1.5 },
                      flexGrow: { xs: 1, sm: 0 }
                    }}
                  >
                    Reset
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={isLoading}
                    startIcon={isLoading ? <CircularProgress size={15} color="inherit" /> :
                      <FontAwesomeIcon className='text-xs' icon={faSearch}></FontAwesomeIcon>}
                    sx={{
                      minWidth: 85,
                      // py: 0,
                      borderRadius: 2,
                      fontWeight: 500,
                      textTransform: 'none',
                      // fontSize:"0.7rem",
                      flexGrow: { xs: 1, sm: 0 }
                    }}
                  >
                    {isLoading ? 'Loading' : 'Search'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>

        {/* Error Message */}
        {isError && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            <AlertTitle>Error</AlertTitle>
            {error.message}
          </Alert>
        )}

        {/* Data Table Section */}
        <Paper sx={{
          p: isMobile ? 1 : 1,
          borderRadius: 3,
          boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08)',
          backgroundColor: 'background.paper'
        }} ref={tableRef}>
          {/* Table Header */}
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            mb: 0,
            gap: 1,
            p: 1
          }}>
            <Typography variant="h6" sx={{
              fontWeight: 600,
              color: 'text.primary',
              fontSize: "0.8rem"
            }}>
              {reportData.length > 0 ?
                `Showing ${filteredData.length} of ${reportData.length} records` :
                'No data available'}
            </Typography>

            {reportData.length > 0 && (
              <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                justifyContent: { xs: 'flex-start', sm: 'flex-end' }
              }}>
                <Chip
                  label={`Type: ${filters.Vou_Type === 'R' ? 'Receive' : 'Payment'}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 500 }}
                />
                <Chip
                  label={`From: ${format(filters.fromDate, 'dd-MMM-yyyy')}`}
                  size="small"
                  color="secondary"
                  variant="outlined"
                  sx={{ fontWeight: 500 }}
                />
                <Chip
                  label={`To: ${format(filters.toDate, 'dd-MMM-yyyy')}`}
                  size="small"
                  color="secondary"
                  variant="outlined"
                  sx={{ fontWeight: 500 }}
                />
              </Box>
            )}
          </Box>

          {/* Data Table */}
          {reportData.length > 0 ? (
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: 'calc(100vh - 320px)',
                overflow: 'auto',
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                '&::-webkit-scrollbar': {
                  height: 8,
                  width: 8
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: theme.palette.grey[400],
                  borderRadius: 4
                }
              }}
            >
              <Table stickyHeader size="small" sx={{ minWidth: 650, }}>
                <TableHead>
                  <TableRow>
                    {groupBy === 'account' ? (
                      <>
                        <TableCell sx={{
                         ...headCssStyle,
                          minWidth: 200,

                          position: 'sticky',
                          left: 0,
                          zIndex: 2
                        }}>
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <span >Account</span>
                            <IconButton
                              size="small"
                              onClick={(e) => handleFilterClick('AC_DESC', e)}
                              sx={{
                                p: 0.5,
                                ml: 0.5,
                                color: columnFilters['AC_DESC'] ? 'primary.main' : 'inherit'
                              }}
                            >
                              <FontAwesomeIcon icon={faFilter} className={headfilterStyle} />
                              {/* <FilterList fontSize="small" /> */}
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell sx={{
                         ...headCssStyle,
                          minWidth: 150
                        }}>
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <span>Amount</span>
                            <IconButton
                              size="small"
                              onClick={(e) => handleFilterClick('Chq_Amt', e)}
                              sx={{
                                p: 0.5,
                                ml: 0.5,
                                color: columnFilters['Chq_Amt'] ? 'primary.main' : 'inherit'
                              }}
                            >
                              <FontAwesomeIcon icon={faFilter} className={headfilterStyle} />
                              {/* <FilterList fontSize="small" /> */}
                            </IconButton>
                          </Box>
                        </TableCell>
                      </>
                    ) : groupBy === 'party' ? (
                      <>
                        <TableCell sx={{
                         ...headCssStyle,
                          minWidth: 200,
                          position: 'sticky',
                          left: 0,
                          zIndex: 2
                        }}>
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <span>Party</span>
                            <IconButton
                              size="small"
                              onClick={(e) => handleFilterClick('PAc_Desc', e)}
                              sx={{
                                p: 0.5,
                                ml: 0.5,
                                color: columnFilters['PAc_Desc'] ? 'primary.main' : 'inherit'
                              }}
                            >
                              <FontAwesomeIcon icon={faFilter} className={headfilterStyle} />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell sx={{
                          ...headCssStyle,
                          minWidth: 150
                        }}>
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <span>Amount</span>
                            <IconButton
                              size="small"
                              onClick={(e) => handleFilterClick('Chq_Amt', e)}
                              sx={{
                                p: 0.5,
                                ml: 0.5,
                                color: columnFilters['Chq_Amt'] ? 'primary.main' : 'inherit'
                              }}
                            >
                              <FontAwesomeIcon icon={faFilter} className={headfilterStyle} />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </>
                    ) : groupBy === 'date' || groupBy === 'month' ? (
                      <>
                        <TableCell sx={{
                          ...headCssStyle,
                          minWidth: 150,
                          position: 'sticky',
                          left: 0,
                          zIndex: 2
                        }}>
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <span>{groupBy === 'month' ? 'Month' : 'Date'}</span>
                            <IconButton
                              size="small"
                              onClick={(e) => handleFilterClick('Chq_Date', e)}
                              sx={{
                                p: 0.5,
                                ml: 0.5,
                                color: columnFilters['Chq_Date'] ? 'primary.main' : 'inherit'
                              }}
                            >
                              <FontAwesomeIcon icon={faFilter} className={headfilterStyle} />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell sx={{
                          backgroundColor: 'background.paper',
                          fontWeight: 600,
                          color: 'text.secondary',
                          minWidth: 150
                        }}>
                          <Box display="flex" alignItems="center" justifyContent="space-between" >
                            <span>Amount</span>
                            <IconButton
                              size="small"
                              onClick={(e) => handleFilterClick('Chq_Amt', e)}
                              sx={{
                                p: 0.5,
                                ml: 0.5,
                                color: columnFilters['Chq_Amt'] ? 'primary.main' : 'inherit'
                              }}
                            >
                              <FontAwesomeIcon icon={faFilter} className={headfilterStyle} />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </>
                    ) : (
                      allColumns.map((column) => (
                        <TableCell
                          key={column.key}
                          sx={{
                           ...headCssStyle,
                            minWidth: column.key === 'AC_DESC' || column.key === 'PAc_Desc' ? 180 :
                              column.key === 'Chq_Date' ? 120 :
                                column.key === 'Chq_Amt' ? 150 : 100
                          }}
                        >
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <span>{column.label}</span>
                            <IconButton
                              size="small"
                              onClick={(e) => handleFilterClick(column.key, e)}
                              sx={{
                                p: 0.5,
                                ml: 0.5,
                                color: columnFilters[column.key] ? 'primary.main' : 'inherit'
                              }}
                            >
                              <FontAwesomeIcon icon={faFilter} className={headfilterStyle} />
                            </IconButton>
                          </Box>
                        </TableCell>
                      ))
                    )}
                  </TableRow>
                </TableHead>

                <TableBody sx={{ fontSize: "0.8rem" }}>
                  {filteredAndGroupedData.length > 0 ? (
                    filteredAndGroupedData.map((row, index) => (
                      <TableRow
                        key={index}
                        hover
                        sx={{
                          '&:nth-of-type(odd)': {
                            backgroundColor: 'action.hover'
                          },
                          '&:last-child td, &:last-child th': {
                            borderBottom: 0
                          },

                        }}
                      >
                        {groupBy === 'account' ? (
                          <>
                            <TableCell sx={{
                              position: 'sticky',
                              left: 0,
                              fontSize: "0.7rem",
                              backgroundColor: index % 2 === 0 ? 'action.hover' : 'background.paper'
                            }}>
                              {row.AC_DESC}
                            </TableCell>
                            <TableCell>
                              {parseFloat(row.Chq_Amt).toLocaleString('en-IN', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}
                            </TableCell>
                          </>
                        ) : groupBy === 'party' ? (
                          <>
                            <TableCell sx={{
                              position: 'sticky',
                              left: 0,
                              fontSize: "0.7rem",
                              backgroundColor: index % 2 === 0 ? 'action.hover' : 'background.paper'
                            }}>
                              {row.PAc_Desc}
                            </TableCell>
                            <TableCell>
                              {parseFloat(row.Chq_Amt).toLocaleString('en-IN', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}
                            </TableCell>
                          </>
                        ) : groupBy === 'bank' ? (
                          <>
                            <TableCell sx={{
                              position: 'sticky',
                              left: 0,
                              backgroundColor: index % 2 === 0 ? 'action.hover' : 'background.paper'
                            }}>
                              {row.BankName}
                            </TableCell>
                            <TableCell>
                              {parseFloat(row.Chq_Amt).toLocaleString('en-IN', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}
                            </TableCell>
                          </>
                        ) : groupBy === 'date' || groupBy === 'month' ? (
                          <>
                            <TableCell sx={{
                              position: 'sticky',
                              left: 0,
                              backgroundColor: index % 2 === 0 ? 'action.hover' : 'background.paper'
                            }}>
                              {formatDateSafe(row.Chq_Date, groupBy === 'month' ? 'MMM yyyy' : 'dd/MM/yyyy')}
                            </TableCell>
                            <TableCell>
                              {parseFloat(row.Chq_Amt).toLocaleString('en-IN', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell>{formatDateSafe(row.Chq_Date)}</TableCell>
                            <TableCell sx={{ fontSize: "0.7rem", }}>{row.AC_DESC}</TableCell>
                            <TableCell sx={{ fontSize: "0.7rem", }}>{row.PAc_Desc}</TableCell>
                            <TableCell>{row.BankName}</TableCell>
                            <TableCell>{row.Chq_No}</TableCell>
                            <TableCell>
                              {parseFloat(row.Chq_Amt).toLocaleString('en-IN', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.7rem" }}>{row.Remark}</TableCell>
                          </>
                        )}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={groupBy === 'none' ? 8 : 3}
                        align="center"
                        sx={{ py: 4 }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <FontAwesomeIcon className='text-gray-100 text-48' icon={faInbox}></FontAwesomeIcon>
                          {/* <Inbox sx={{ fontSize: 48, color: 'text.disabled' }} /> */}
                          <Typography variant="body1" color="textSecondary">
                            No matching records found
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>

                {/* Totals Row */}
                {filteredAndGroupedData.length > 0 && (
                  <TableFooter>
                    <TableRow sx={{
                      backgroundColor: theme.palette.grey[200],
                      '& td': {
                        fontWeight: 700,
                        fontSize: 14,
                        color: 'text.primary'
                      }
                    }}>
                      {groupBy === 'account' ? (
                        <>
                          <TableCell>GRAND TOTAL</TableCell>
                          <TableCell>
                            {totals.Chq_Amt.toLocaleString('en-IN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </TableCell>
                        </>
                      ) : groupBy === 'party' ? (
                        <>
                          <TableCell>GRAND TOTAL</TableCell>
                          <TableCell>
                            {totals.Chq_Amt.toLocaleString('en-IN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </TableCell>
                        </>
                      ) : groupBy === 'date' || groupBy === 'month' ? (
                        <>
                          <TableCell>GRAND TOTAL</TableCell>
                          <TableCell>
                            {totals.Chq_Amt.toLocaleString('en-IN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell colSpan={5}>GRAND TOTAL</TableCell>
                          <TableCell>
                            {totals.Chq_Amt.toLocaleString('en-IN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </TableCell>
                          <TableCell></TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableFooter>
                )}
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              backgroundColor: 'background.paper',
              borderRadius: 2
            }}>
              <FontAwesomeIcon icon={faInfo} className='text-gray-100' ></FontAwesomeIcon>
              {/* <DescriptionOutlined sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} /> */}
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Data Available
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, textAlign: 'center' }}>
                {isLoading ? 'Loading data...' : 'Please Apply Different Filters'}
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Filter Popover */}
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleFilterClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center'
          }}
          PaperProps={{
            sx: {
              p: 2,
              width: 300,
              borderRadius: 2,
              boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.2)'
            }
          }}
        >
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Filter by {currentFilterColumn}
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={columnFilters[currentFilterColumn] || ''}
            onChange={handleColumnFilterChange}
            InputProps={{
              endAdornment: columnFilters[currentFilterColumn] && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => clearColumnFilter(currentFilterColumn)}>
                    <FontAwesomeIcon icon={faClose} className='text-sm' />
                    {/* <Clear fontSize="small" /> */}
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ mb: 2 }}
          />
        </Popover>
      </Box>
    </MainContainer>
  );
};

export default CreatePaymentReport;